import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useGeneratorContext } from '../contexts/GeneratorStateContext';
import { useNotification } from '../components/NotificationSystem';
import PageTemplate from '../components/layout/PageTemplate';
import ActivityLog from '../components/profile/ActivityLog';
import AdminAnalytics from '../components/profile/AdminAnalytics';
import GenDefaults from '../components/profile/GenDefaults';
import LocalBuildsImport from '../components/profile/LocalBuildsImport';
import { localGenPath } from '../utils/buildRoutes';
import './Profile.css';

const TXT_PROFILE = { fr: 'Profil', en: 'Profile', de: 'Profil', es: 'Perfil', pt: 'Perfil' };
const TXT_NOT_CONNECTED = { fr: 'Non connecté.', en: 'Not connected.', de: 'Nicht angemeldet.', es: 'No conectado.', pt: 'Não conectado.' };
const TXT_CONNECT_WITH_DISCORD = { fr: 'Se connecter avec Discord', en: 'Connect with Discord', de: 'Mit Discord anmelden', es: 'Iniciar sesión con Discord', pt: 'Entrar com Discord' };
const TXT_DISCONNECT = { fr: 'Se deconnecter', en: 'Disconnect', de: 'Abmelden', es: 'Cerrar sesión', pt: 'Sair' };
const TXT_BUILD_COUNT = { fr: 'Nombre de stuffs', en: 'Build count', de: 'Anzahl der Builds', es: 'Número de builds', pt: 'Número de builds' };
const TXT_LOGGED_OUT = { fr: 'Deconnecte.', en: 'Logged out.', de: 'Abgemeldet.', es: 'Sesión cerrada.', pt: 'Sessão encerrada.' };
const TXT_LOADING = { fr: 'Chargement...', en: 'Loading...', de: 'Wird geladen...', es: 'Cargando...', pt: 'Carregando...' };
const TXT_AVATAR_ALT = { fr: 'Avatar du profil', en: 'Profile avatar', de: 'Profilbild', es: 'Avatar del perfil', pt: 'Avatar do perfil' };

const profileNavTitle = (lang) => (TXT_PROFILE[lang] || TXT_PROFILE.en);

function Profile({ lang }) {
    const { authState, isAuthenticated, isAdmin, user, startLogin, logout } = useAuth();
    const { builds } = useGeneratorContext();
    const navigate = useNavigate();
    const location = useLocation();
    const { showNotification } = useNotification();

    const notConnected = TXT_NOT_CONNECTED[lang] || TXT_NOT_CONNECTED.en;
    const connectLabel = TXT_CONNECT_WITH_DISCORD[lang] || TXT_CONNECT_WITH_DISCORD.en;
    const disconnectLabel = TXT_DISCONNECT[lang] || TXT_DISCONNECT.en;
    const buildsLabel = TXT_BUILD_COUNT[lang] || TXT_BUILD_COUNT.en;

    const onConnect = () => {
        startLogin(`${location.pathname}${location.search}`);
    };

    const onDisconnect = async () => {
        await logout();
        showNotification(TXT_LOGGED_OUT[lang] || TXT_LOGGED_OUT.en, 'info');
        navigate(localGenPath(lang), { replace: true });
    };

    const avatar = user?.avatar_url || '/logo192.png';
    const displayName = user?.display_name || user?.discord_username || 'User';
    const remoteBuildCount = builds.filter(entry => entry?.scope === 'remote').length;

    const headerLabel = authState === 'loading'
        ? profileNavTitle(lang)
        : isAuthenticated
            ? displayName
            : profileNavTitle(lang);

    return (
        <PageTemplate
            header={(
                <h1 className={`page-template-title${isAuthenticated ? ' profile-title' : ''}`}>
                    {headerLabel}
                </h1>
            )}
        >
            <div className="profile-page">
            {authState === 'loading' && <p>{TXT_LOADING[lang] || TXT_LOADING.en}</p>}

            {authState !== 'loading' && isAuthenticated && (
                <>
                    {/* Admin first: for an admin this page is a dashboard, and the
                        account block is the part that never changes. */}
                    {isAdmin && <ActivityLog lang={lang} />}
                    {isAdmin && <AdminAnalytics lang={lang} />}
                    <img className="profile-avatar" src={avatar} alt={TXT_AVATAR_ALT[lang] || TXT_AVATAR_ALT.en} />
                    <p className="profile-count">{buildsLabel}: {remoteBuildCount}</p>
                    <button type="button" className="profile-action danger" onClick={onDisconnect}>
                        {disconnectLabel}
                    </button>
                    {/* Hides itself when this browser holds nothing to import. */}
                    <LocalBuildsImport lang={lang} />
                </>
            )}

            {authState !== 'loading' && !isAuthenticated && (
                <>
                    <p className="profile-status">{notConnected}</p>
                    <button type="button" className="profile-action" onClick={onConnect}>
                        {connectLabel}
                    </button>
                </>
            )}

            {/* Stored on the device, so it is worth offering signed in or not. */}
            {authState !== 'loading' && <GenDefaults lang={lang} />}
        </div>
        </PageTemplate>
    );
}

export default Profile;
